import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов обоев
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Получение названия типа обоев
  const getWallpaperTypeName = (typeId) => {
    const wallpaperTypes = {
      'standard': 'Стандартные бумажные',
      'wide': 'Флизелиновые широкие',
      'vinyl': 'Виниловые',
      'nonwoven': 'Флизелиновые',
      'textile': 'Текстильные',
      'photoWall': 'Фотообои',
      'liquid': 'Жидкие обои'
    };
    return wallpaperTypes[typeId] || 'Обои';
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сохраненные расчеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты обоев для разных комнат.
          Нажмите "Сохранить расчет" после вычисления, чтобы добавить его сюда.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сохраненные расчеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'расчет', 'расчета', 'расчетов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип обоев:</span>
                <span className="font-medium">
                  {getWallpaperTypeName(calc.formData.wallpaperType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Размеры:</span>
                <span>
                  {calc.formData.roomLength}×{calc.formData.roomWidth}×{calc.formData.roomHeight} м
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Рулонов:</span>
                <span className="font-medium">
                  {calc.results.rollsRequired} шт.
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(calc.results.totalCost, 0)} ₽
                </span>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
              {formatDate(calc.date)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default SavedCalculationsSidebar;